'use client';
import {
  Box,
  Button,
  Checkbox,
  Dropdown,
  FormControl,
  FormHelperText,
  formHelperTextClasses,
  FormLabel,
  Input,
  Option,
  Typography,
  useNiceModal,
  useBreakpoints,
} from '@castlery/fortress';
import { Error } from '@castlery/fortress/Icons';
import { useCreateCustomerAddressMutation, useUpdateCustomerAddressMutation } from '@castlery/modules-user-domain';
import { LocalesNamespace, useTranslation } from '@castlery/monorepo-i18n';
import { CustomerAddressEntity, CustomerAddressEntity_V2 } from '@castlery/types';
import React, { useMemo, useState } from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { getVersionFormData } from './get-version-form-data';
import { CUSTOMER_DEFAULT_FIRSTNAME, CUSTOMER_DEFAULT_LASTNAME } from '@castlery/config';

export const addressFormClasses = {
  footer: 'address-form-footer',
};

export enum AddressFormActionType {
  ADD = 'ADD',
  UPDATE = 'UPDATE',
}

export interface AddressFormProps {
  /**
   * The callback function when the user clicks the cancel button
   */
  onCancel: () => void;
  /**
   * The callback function when the user saves the address
   * @param addressId Optional, if the action type is `ADD`, the addressId is undefined
   * @returns
   */
  onSaved?: (addressId?: number) => Promise<void>;
  /**
   * The fields of new version form use camelCase format, old version form uses snake case format
   * @default false
   */
  useNewVersion?: boolean;
  /**
   * The action type of the form, `ADD` or `UPDATE`.
   * @param ADD If actionType is `ADD`, the form is submitted through the create address API.
   * @param UPDATE If actionType is `UPDATE`, the form is submitted through the update address API.
   * @default `ADD`
   */
  actionType?: AddressFormActionType;
  /**
   * The ref of the form
   * @default null
   */
  formRef?: React.RefObject<HTMLFormElement>;
  /**
   * The default address of the form
   * @default null
   */
  defaultAddress?: CustomerAddressEntity | CustomerAddressEntity_V2;
  /**
   * The text of the save button
   * @default 'SAVE'
   */
  saveButtonText?: string;
}

export function AddressForm({
  useNewVersion = false,
  actionType = AddressFormActionType.ADD,
  formRef,
  defaultAddress,
  onCancel,
  onSaved,
  saveButtonText,
}: AddressFormProps) {
  const formList = getVersionFormData(useNewVersion);
  const addressFromRef = formRef || null;
  const { desktop } = useBreakpoints();
  const { t } = useTranslation(LocalesNamespace.SHARED, { keyPrefix: 'addressForm' });

  const [floorUnitCheckboxChecked, setFloorUnitCheckboxChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updateCustomerAddress] = useUpdateCustomerAddressMutation();
  const [createCustomerAddress] = useCreateCustomerAddressMutation();
  const [modal, modalContextHolder] = useNiceModal();

  const saveTrigger = useMemo(() => {
    if (actionType === AddressFormActionType.ADD) {
      return createCustomerAddress;
    } else {
      return updateCustomerAddress;
    }
  }, [actionType, createCustomerAddress, updateCustomerAddress]);

  // Generate defaultValues, prefer redux user, then field default value
  const defaultValues = React.useMemo(() => {
    if (!defaultAddress) {
      return Object.fromEntries(formList.map((f) => [f.key, 'value' in f ? f.value ?? undefined : undefined]));
    }
    const defaultFormData = defaultAddress;
    const { firstname, lastname } = defaultFormData;
    const name = `${firstname ?? ''}${lastname ?? ''}`.trim().toUpperCase();
    const upperDefaultName = `${CUSTOMER_DEFAULT_FIRSTNAME}${CUSTOMER_DEFAULT_LASTNAME}`.trim().toUpperCase();

    if (name && name === upperDefaultName) {
      defaultFormData.firstname = '';
      defaultFormData.lastname = '';
    }

    return Object.fromEntries(
      formList.map((f) => [
        f.key,
        'defaultValueReWrite' in f && f.defaultValueReWrite
          ? f.defaultValueReWrite(defaultFormData)
          : 'value' in f
          ? (defaultFormData as any)[f.key] ?? f.value ?? undefined
          : (defaultFormData as any)[f.key] ?? undefined,
      ])
    );
  }, [defaultAddress, formList]);

  const {
    handleSubmit,
    control,
    unregister,
    register,
    formState: { errors, isDirty },
  } = useForm({
    mode: 'all',
    defaultValues,
  });

  // 根据floorUnitCheckboxChecked, 如果为空，修改addressFormData， 过滤掉unit和floor项
  // 如果为true，则显示 unit和floor项
  const filteredAddressFormData = useMemo(() => {
    return !floorUnitCheckboxChecked
      ? formList
      : formList.filter((field) => field.key !== 'level' && field.key !== 'flat');
  }, [floorUnitCheckboxChecked, formList]);

  // Validation rules generator
  const getRules = (field: any) => {
    const rules: any = {};

    if (field.required) rules.required = t('errorMessage.required');

    if (field.validation?.minLength) {
      rules.minLength = {
        value: field.validation.minLength,
        message: t('errorMessage.minLength', { minLength: field.validation.minLength }),
      };
    }
    if (field.validation?.maxLength) {
      rules.maxLength = {
        value: field.validation.maxLength,
        message: t('errorMessage.maxLength', { maxLength: field.validation.maxLength }),
      };
    }
    if (field.validation?.pattern) {
      rules.pattern = {
        value: field.validation.pattern,
        message: t('errorMessage.pattern'),
      };
    }
    if (field.validation?.validate) {
      rules.validate = (value: string) => {
        const isNotEmpty = value !== undefined && value !== null && value !== '';
        const isValid = isNotEmpty ? field.validation.validate(value) : true;
        return isValid
          ? isValid
          : field.key === 'zipcode'
          ? t('errorMessage.zipcodeInvalid')
          : field.key === 'phone' || field.key === 'alternatePhone'
          ? t('errorMessage.phoneInvalid')
          : t('errorMessage.validate');
      };
    }
    return rules;
  };
  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFloorUnitCheckboxChecked(e.target.checked);
    if (e.target.checked) {
      unregister('level');
      unregister('flat');
    } else {
      register('level', { required: true });
      register('flat', { required: true });
    }
  };

  const onSubmit = async (data: FieldValues) => {
    console.log('onSubmit data:', data);
    try {
      setLoading(true);
      let addressId: number | undefined;

      if (defaultAddress) {
        data = {
          ...defaultAddress,
          ...data,
        };
        addressId = defaultAddress.id;
      }
      data.state = data.state ?? data.state_name ?? '';
      data.street = data.street ?? data.address1 ?? '';
      const res = await saveTrigger({ address: data });
      if (res.error) {
        let errorDetail = t('errorPrompt.content');
        if ('data' in res.error && (res.error as any).data?.errors?.[0]?.detail) {
          errorDetail = (res.error as any).data.errors[0].detail;
        }
        throw Error(errorDetail);
      }
      await onSaved?.(addressId);
      onCancel?.();
    } catch (e) {
      // Show error modal, consider moving to API layer
      modal.warning({
        title: t('errorPrompt.title'),
        content: (e as any).message || t('errorPrompt.content'),
        showCancelBtn: false,
        confirmText: t('errorPrompt.confirmText'),
      });
    } finally {
      setLoading(false);
    }
  };
  const handleCancel = () => {
    if (!isDirty) {
      onCancel?.();
      return;
    }
    modal.confirmation({
      title: t('unsavedChanges.title'),
      desc: t('unsavedChanges.content'),
      cancelText: t('unsavedChanges.cancelText'),
      confirmText: t('unsavedChanges.confirmText'),
      onCancel: () => {
        onCancel?.();
      },
    });
  };

  return (
    <>
      <form ref={addressFromRef} onSubmit={handleSubmit(onSubmit)} style={{ width: 'inherit' }} noValidate>
        <Box
          sx={{
            py: 4,
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            alignItems: 'flex-end',
            rowGap: { xs: 4, sm: 5 },
            columnGap: { sm: 4 },
          }}
        >
          {filteredAddressFormData.map((field) => (
            <Box key={field.key} mb={2} sx={{ ...field.layoutStyle }}>
              {field.type !== 'checkbox' ? (
                <Controller
                  name={field.key}
                  control={control}
                  rules={getRules(field)}
                  render={({ field: rhfField }) => (
                    <FormControl
                      required={field.required}
                      disabled={field.disabled}
                      sx={{
                        gap: 1,
                        [`& .${formHelperTextClasses.root}`]: {
                          mt: 5,
                        },
                      }}
                    >
                      <FormLabel>
                        <Typography level="body2">{t(`${field.translationKey}.label`)}</Typography>
                      </FormLabel>
                      {field.type === 'input' && (
                        <Input
                          variant="borderplain"
                          {...rhfField}
                          placeholder={t(`${field.translationKey}.placeholder`)}
                          disabled={field.disabled}
                          error={!!errors[field.key]}
                          value={rhfField.value}
                          onChange={(e) => {
                            if (field.formatter) {
                              // console.log('formatter----', field, field.formatter(e.target.value));
                              rhfField.onChange(field.formatter(e.target.value));
                            } else {
                              rhfField.onChange(e.target.value);
                            }
                          }}
                        />
                      )}
                      {field.type === 'select' && (
                        <Dropdown
                          variant="form"
                          {...rhfField}
                          placeholder={t(`${field.translationKey}.placeholder`)}
                          value={rhfField.value}
                          renderValue={() => rhfField.value}
                          onChange={(_, value) => rhfField.onChange(value)}
                          disabled={field.disabled}
                        >
                          {field.options?.map((opt) => (
                            <Option key={opt.value} value={opt.value}>
                              {opt.label}
                            </Option>
                          ))}
                        </Dropdown>
                      )}
                      {errors[field.key] && (
                        <FormHelperText sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Error width={20} height={20} />
                          {(errors[field.key] as any)?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              ) : (
                <Checkbox
                  {...field}
                  label={t(`${field.translationKey}.label`)}
                  variant="outlined"
                  checked={floorUnitCheckboxChecked}
                  onChange={onCheckboxChange}
                />
              )}
            </Box>
          ))}
        </Box>
        <Box
          className={addressFormClasses.footer}
          sx={{
            py: 6,
            ...(desktop
              ? {
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }
              : {
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  flexGrow: 1,
                }),
          }}
        >
          <Button variant="outlined" type="button" onClick={handleCancel}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={loading} loading={loading}>
            {saveButtonText || t('save')}
          </Button>
        </Box>
      </form>
      {modalContextHolder}
    </>
  );
}

export default AddressForm;
